import { NoteType, ResponsiveNoteTemplateVisitType } from "@rebuild/shared";
import RestAPI from "../../utils/restAPI";
import type {
	ResponsiveNoteTemplate,
	ResponsiveNoteTemplateType,
	SaveResponsiveNoteTemplateBody,
} from "./responsiveNoteTemplateForm";

const RESPONSIVE_NOTE_TEMPLATES_ROUTE = "/rest/tools/managedResponsiveNoteTemplates";

function buildTemplateQuery(
	organizationId: string,
	noteType?: NoteType,
	type?: ResponsiveNoteTemplateType,
	visitType?: ResponsiveNoteTemplateVisitType,
): string {
	const params = new URLSearchParams();
	params.set("organizationId", organizationId);
	if (noteType) {
		params.set("noteType", noteType);
	}
	if (type) {
		params.set("type", type);
	}
	if (visitType) {
		params.set("visitType", visitType);
	}
	return params.toString();
}

function requireTemplateResult<T>(result: T | null | undefined, action: "fetch" | "save" | "archive"): T {
	if (result == null) {
		throw new Error(`Failed to ${action} responsive note template`);
	}
	return result;
}

export async function fetchResponsiveNoteTemplates(params: {
	organizationId: string;
	noteType?: NoteType;
	type?: ResponsiveNoteTemplateType;
	visitType?: ResponsiveNoteTemplateVisitType;
}): Promise<ResponsiveNoteTemplate[]> {
	const query = buildTemplateQuery(params.organizationId, params.noteType, params.type, params.visitType);
	return requireTemplateResult(
		await RestAPI.fetchRebuildAPI(`${RESPONSIVE_NOTE_TEMPLATES_ROUTE}?${query}`, "GET", {}, "data", true, false),
		"fetch",
	);
}

export async function saveResponsiveNoteTemplate(
	organizationId: string,
	body: SaveResponsiveNoteTemplateBody,
): Promise<ResponsiveNoteTemplate> {
	const query = buildTemplateQuery(organizationId);
	if (body.id) {
		return requireTemplateResult(
			await RestAPI.fetchRebuildAPI(
				`${RESPONSIVE_NOTE_TEMPLATES_ROUTE}/${body.id}?${query}`,
				"PATCH",
				body,
				"data",
				true,
				false,
			),
			"save",
		);
	}
	return requireTemplateResult(
		await RestAPI.fetchRebuildAPI(`${RESPONSIVE_NOTE_TEMPLATES_ROUTE}?${query}`, "POST", body, "data", true, false),
		"save",
	);
}

export async function archiveResponsiveNoteTemplate(organizationId: string, templateId: string): Promise<void> {
	const query = buildTemplateQuery(organizationId);
	requireTemplateResult(
		await RestAPI.fetchRebuildAPI(
			`${RESPONSIVE_NOTE_TEMPLATES_ROUTE}/${templateId}?${query}`,
			"DELETE",
			{},
			"data",
			true,
			false,
		),
		"archive",
	);
}
